import type { ApiFetcherArgs } from "@ts-rest/core";
import type { Operator } from "~domain/operators/models/operator-model.ts";

import * as Sentry from "@sentry/vue";
import { DomainError } from "~domain/global/errors/domain-error.ts";
import { loadConfig } from "~ui/@application/configs/runtime-config.ts";
import { useConfig } from "~ui/@application/provider.ts";
import { isDefined } from "~utils/collection.ts";
import { isStatusCodeSuccess } from "~utils/http-utils.ts";

export async function sentryLogBackofficeTsRestException(
  response: { status: number; body?: unknown; headers: Headers },
  args: ApiFetcherArgs,
) {
  const config = loadConfig();

  if (!config.VITE_SENTRY_ENABLED) {
    return;
  }

  if (!isStatusCodeSuccess(response.status)) {
    const transactionId = response.headers.get("x-voltalis-transaction-id");

    Sentry.withScope((scope) => {
      scope.setTag("api_client", "ts-rest");
      scope.setTag("status_code", response.status);
      if (transactionId) {
        scope.setTag("transaction_id", transactionId);
      }
      scope.setExtra("path", args.path);
      scope.setExtra("method", args.method);
      scope.setExtra("request_body", response.body);
      scope.setExtra("response_body", response.body);
      Sentry.captureException(new Error(`HTTP Error ${response.status} at ${args.path}`));
    });
  }
}

export async function sentryLogBackofficeSdkException(response: Response) {
  const config = loadConfig();

  if (!config.VITE_SENTRY_ENABLED) {
    return;
  }

  if (!isStatusCodeSuccess(response.status)) {
    const transactionId = response.headers.get("x-voltalis-transaction-id");
    let requestBody: string | undefined;
    let responseBody: string | undefined;

    try {
      requestBody = await response.clone().text();
    } catch {
      requestBody = "Could not read request body";
    }

    try {
      responseBody = await response.clone().text();
    } catch {
      responseBody = "Could not read response body";
    }

    Sentry.withScope((scope) => {
      scope.setTag("api_client", "backoffice-sdk");
      scope.setTag("status_code", response.status);
      if (transactionId) {
        scope.setTag("transaction_id", transactionId);
      }
      scope.setExtra("url", response.url);
      scope.setExtra("request_body", requestBody);
      scope.setExtra("response_body", responseBody);
      Sentry.captureException(new Error(`HTTP Error ${response.status} at ${response.url}`));
    });
  }
}

export function useSentry() {
  const { VITE_SENTRY_ENABLED } = useConfig();

  function captureException(error: Error) {
    if (!VITE_SENTRY_ENABLED) {
      return;
    }

    Sentry.withScope((scope) => {
      if (error instanceof DomainError) {
        scope.setTag("error_type", "domain_error");
        scope.setTag("error_code", error.errorCode);
      } else {
        scope.setTag("error_type", "unexpected_error");
      }
      Sentry.captureException(error);
    });
  }

  function setUser(operator?: Operator | null) {
    if (!VITE_SENTRY_ENABLED) {
      return;
    }

    if (!operator) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: operator.id,
      email:
        operator.firstname && operator.lastname
          ? `${operator.firstname}.${operator.lastname}@voltalis.com`.toLowerCase()
          : undefined,
      username: [operator.firstname, operator.lastname].filter(isDefined).join(" "),
    });
  }

  return {
    sentry: Sentry,
    enabled: VITE_SENTRY_ENABLED,
    captureException,
    setUser,
  };
}
